import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";

import { CartProvider } from "@/lib/CartContext";
import { PirateProvider } from "@/lib/PirateContext";
import { CustomerProvider } from "@/lib/CustomerContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import NewsletterModal from "@/components/NewsletterModal";
import PirateHUD from "@/components/PirateHUD";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import CrewHierarchySheet from "@/components/CrewHierarchySheet";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import CustomerAuth from "@/pages/CustomerAuth";
import CustomerAccount from "@/pages/CustomerAccount";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

// --- Shopify-compatible URL aliases ---
// Shopify's admin "View product/collection" buttons generate URLs following
// its canonical paths. We map them to the app's own routes so the storefront
// integration keeps working even when Shopify links back to desashop.net.

function ShopifyProductAlias() {
    // /products/:handle → /figura/:handle (same component, preserves search)
    const { handle } = useParams();
    const { search } = useLocation();
    return <Navigate to={`/figura/${handle}${search}`} replace />;
}

function ShopifyCollectionAlias() {
    // /collections/:handle → /figure?category=:handle
    const { handle } = useParams();
    return <Navigate to={`/figure?category=${encodeURIComponent(handle)}`} replace />;
}

function ShopifyPreviewRedirect() {
    // /online_store_preview?product_handle=X  (Shopify admin preview link)
    // Also handles ?handle=, ?preview_theme_id=, or falls back to home.
    const [params] = useSearchParams();
    const handle =
        params.get("product_handle") ||
        params.get("handle") ||
        params.get("preview_product_handle");
    if (handle) {
        return <Navigate to={`/figura/${handle}`} replace />;
    }
    return <Navigate to="/" replace />;
}

// --- Catch-all: forward Shopify-owned paths (checkouts, cart permalink,
// orders, pages, policies, blogs, apps proxy) to the myshopify.com host so
// the customer completes the flow on Shopify. Anything else falls back home.
const SHOPIFY_PATH_PREFIXES = [
    "/checkouts/",
    "/checkout/",
    "/cart/c/",
    "/orders/",
    "/pages/",
    "/policies/",
    "/blogs/",
    "/apps/",
];

function CatchAllRoute() {
    const { pathname, search, hash } = useLocation();
    const shopifyHost = process.env.REACT_APP_SHOPIFY_DOMAIN;
    const isShopifyPath =
        !!shopifyHost &&
        SHOPIFY_PATH_PREFIXES.some((p) => pathname.startsWith(p));
    useEffect(() => {
        if (!isShopifyPath) return;
        window.location.replace(
            `https://${shopifyHost}${pathname}${search}${hash}`
        );
    }, [isShopifyPath, shopifyHost, pathname, search, hash]);
    if (isShopifyPath) {
        return (
            <div
                className="min-h-[40vh] flex items-center justify-center font-mono text-sm text-ink/60"
                data-testid="shopify-forward"
            >
                salpando verso Shopify…
            </div>
        );
    }
    return <Navigate to="/" replace />;
}

function Shell() {
    const location = useLocation();
    const isAdmin = location.pathname.startsWith("/admin");
    return (
        <>
            <ScrollToTop />
            {!isAdmin && <Navbar />}
            <main>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/figure" element={<Shop />} />
                    <Route path="/figura/:handle" element={<ProductDetail />} />
                    {/* Shopify-compatible aliases (View button, DSers imports, etc.) */}
                    <Route path="/products/:handle" element={<ShopifyProductAlias />} />
                    <Route path="/collections/:handle" element={<ShopifyCollectionAlias />} />
                    <Route path="/collections" element={<Navigate to="/figure" replace />} />
                    <Route path="/online_store_preview" element={<ShopifyPreviewRedirect />} />
                    <Route
                        path="/checkout/success"
                        element={<CheckoutSuccess />}
                    />
                    <Route path="/admin" element={<AdminLogin />} />
                    <Route
                        path="/admin/dashboard"
                        element={<AdminDashboard />}
                    />
                    <Route path="/account/login" element={<CustomerAuth />} />
                    <Route path="/account" element={<CustomerAccount />} />
                    {/* Fallback: forward Shopify paths (checkouts/cart/orders/pages) to myshopify, everything else → home */}
                    <Route path="*" element={<CatchAllRoute />} />
                </Routes>
            </main>
            {!isAdmin && <Footer />}
            {!isAdmin && <CartDrawer />}
            {!isAdmin && <NewsletterModal />}
            {!isAdmin && <PirateHUD />}
            {!isAdmin && <PWAInstallPrompt />}
            {!isAdmin && <CrewHierarchySheet />}
            <Toaster
                position="top-center"
                toastOptions={{
                    style: {
                        background: "#111",
                        color: "#F4EEDD",
                        border: "2px solid #111",
                        borderRadius: 0,
                        fontFamily: "DM Sans, sans-serif",
                        boxShadow: "4px 4px 0 0 #D92E2E",
                    },
                }}
            />
        </>
    );
}

function App() {
    return (
        <div className="App parchment-bg min-h-screen">
            <BrowserRouter>
                <PirateProvider>
                    <CustomerProvider>
                        <CartProvider>
                            <Shell />
                        </CartProvider>
                    </CustomerProvider>
                </PirateProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
